
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { MortgageData } from "../CalculatorTabs";
import { calculateMortgageDataLegacy } from "../utils/legacyCalculatorWrapper";
import { calculateFundingFee } from "../utils/fundingFeeCalculations";
import { AutoCalculateOptions } from "../utils/types";
import { 
  calculateAdjustedClosingCosts,
  safeNumberConversion,
  validateCalculationInputs
} from "../utils/transactionSafeCalculations";
import { 
  calculateSellerCreditAmount,
  validateSellerCreditInput
} from "../utils/sellerCreditCalculations";

// Import our enhanced components
import { EnhancedSellerCreditSection } from "./closingCosts/EnhancedSellerCreditSection";
import ClosingCostsList from "./closingCosts/ClosingCostsList";
import ClosingCostTotal from "./closingCosts/ClosingCostTotal";
import AutoCalculateToggle from "./closingCosts/AutoCalculateToggle";
import { CashDueAtClosing } from "./closingCosts/CashDueAtClosing";
import { LoanOriginationWaiverToggle } from "./closingCosts/LoanOriginationWaiverToggle";
import { EarnestMoneyInput } from "./closingCosts/EarnestMoneyInput";

interface ClosingCostsInputsProps {
  mortgageData: MortgageData;
  setMortgageData: React.Dispatch<React.SetStateAction<MortgageData>>;
  autoCalculate: {
    propertyTax: boolean;
    insurance: boolean;
    pmi: boolean;
    closingCosts: boolean;
  };
  setAutoCalculate: React.Dispatch<React.SetStateAction<{
    propertyTax: boolean;
    insurance: boolean;
    pmi: boolean;
    closingCosts: boolean;
  }>>;
}

const ClosingCostsInputs: React.FC<ClosingCostsInputsProps> = ({ 
  mortgageData, 
  setMortgageData, 
  autoCalculate, 
  setAutoCalculate 
}) => {
  const handleClosingCostChange = (field: keyof MortgageData["closingCosts"], value: number) => {
    const safeValue = safeNumberConversion(value, 0);
    
    if (!isNaN(safeValue) && safeValue >= 0) {
      setMortgageData(prev => {
        const newClosingCosts = {
          ...prev.closingCosts,
          [field]: safeValue
        };
        
        // Recalculate the total with transaction-safe logic
        if (field !== 'total' && field !== 'sellerCreditAmount') {
          const originalTotal = safeNumberConversion(
            newClosingCosts.loanOrigination +
            newClosingCosts.appraisalFee +
            newClosingCosts.titleServices +
            newClosingCosts.governmentRecordingCharges +
            newClosingCosts.prepaids +
            newClosingCosts.initialEscrow +
            newClosingCosts.other +
            (newClosingCosts.fundingFee || 0),
            0
          );
          
          // Recalculate seller credit amount with enhanced logic
          const sellerCreditResult = calculateSellerCreditAmount(
            prev.sellerCredit,
            prev.homePrice,
            originalTotal
          );
          
          newClosingCosts.sellerCreditAmount = sellerCreditResult.effectiveDollarAmount;
          newClosingCosts.total = originalTotal; // Keep original total, not adjusted
        }
        
        // Validate all calculation inputs before updating state
        const calculationInputs = {
          total: newClosingCosts.total,
          sellerCreditAmount: newClosingCosts.sellerCreditAmount
        };
        
        if (!validateCalculationInputs(calculationInputs)) {
          console.warn('Invalid calculation inputs detected, reverting to previous state');
          return prev;
        }
        
        return {
          ...prev,
          closingCosts: newClosingCosts
        };
      });
    }
  };
  
  // Enhanced seller credit handler with proper state synchronization
  const handleSellerCreditChange = (value: number) => {
    console.log('=== SELLER CREDIT DEBUG START ===');
    console.log('1. Input value received:', value);
    console.log('2. Current mortgage data home price:', mortgageData.homePrice);
    
    // Validate input first
    const validation = validateSellerCreditInput(value, mortgageData.homePrice);
    
    if (!validation.isValid) {
      console.warn('4. Invalid seller credit input:', validation.message);
      return;
    }
    
    const safeSellerCreditPercentage = validation.safeValue;
    console.log('5. Safe seller credit percentage:', safeSellerCreditPercentage);
    
    setMortgageData(prev => {
      // Calculate original total of closing costs before seller credit
      const originalTotal = safeNumberConversion(
        prev.closingCosts.loanOrigination +
        prev.closingCosts.appraisalFee +
        prev.closingCosts.titleServices +
        prev.closingCosts.governmentRecordingCharges +
        prev.closingCosts.prepaids +
        prev.closingCosts.initialEscrow +
        prev.closingCosts.other +
        (prev.closingCosts.fundingFee || 0),
        0
      );
      
      console.log('8. Calculated original total before seller credit:', originalTotal);
      
      // Calculate seller credit amount directly from percentage
      const sellerCreditAmount = safeSellerCreditPercentage > 0 ? 
        Math.min((safeSellerCreditPercentage / 100) * prev.homePrice, originalTotal) : 0;
      
      console.log('9. Calculated seller credit amount:', sellerCreditAmount);
      
      // Create the updated data object with proper seller credit amount
      const updatedData = {
        ...prev,
        sellerCredit: safeSellerCreditPercentage,
        closingCosts: {
          ...prev.closingCosts,
          sellerCreditAmount: sellerCreditAmount,
          total: originalTotal
        }
      };
      
      console.log('10. Final updated data:', {
        sellerCredit: updatedData.sellerCredit,
        sellerCreditAmount: updatedData.closingCosts.sellerCreditAmount,
        total: updatedData.closingCosts.total
      });
      console.log('=== SELLER CREDIT DEBUG END ===');
      
      return updatedData;
    });
  };

  const handleWaiverToggle = (waived: boolean) => {
    setMortgageData(prev => ({
      ...prev,
      closingCosts: {
        ...prev.closingCosts,
        loanOriginationWaived: waived,
        loanOrigination: waived ? 0 : (prev.homePrice - prev.downPayment) * 0.01,
        builderName: waived ? prev.closingCosts.builderName : undefined
      }
    }));
  };

  const handleBuilderNameChange = (name: string) => {
    setMortgageData(prev => ({
      ...prev,
      closingCosts: {
        ...prev.closingCosts,
        builderName: name
      }
    }));
  };

  const handleAutoCalculateToggle = (checked: boolean) => {
    setAutoCalculate(prev => {
      const newAutoCalculate = {
        ...prev,
        closingCosts: checked
      };
      
      // If turning auto-calculate on, recalculate closing costs with validation
      if (checked) {
        setMortgageData(prevData => {
          const calculatedData = calculateMortgageDataLegacy(prevData, calculateFundingFee, newAutoCalculate);
          
          // Validate the calculated data before applying
          const calculationInputs = {
            total: calculatedData.closingCosts.total,
            sellerCreditAmount: calculatedData.closingCosts.sellerCreditAmount
          };
          
          if (!validateCalculationInputs(calculationInputs)) {
            console.warn('Invalid auto-calculated closing costs, keeping previous state');
            return prevData;
          }
          
          return calculatedData;
        });
      }
      
      return newAutoCalculate;
    });
  };

  return (
    <div className="space-y-6">
      {/* Main Closing Costs Card */}
      <Card>
        <CardHeader className="flex flex-row justify-between items-center">
          <CardTitle className="text-lg">Closing Costs</CardTitle>
          <AutoCalculateToggle 
            enabled={autoCalculate.closingCosts}
            onToggle={handleAutoCalculateToggle}
          />
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Loan Origination Waiver Toggle */}
          <LoanOriginationWaiverToggle
            isWaived={mortgageData.closingCosts.loanOriginationWaived}
            builderName={mortgageData.closingCosts.builderName}
            loanAmount={mortgageData.homePrice - mortgageData.downPayment}
            onWaiverToggle={handleWaiverToggle}
            onBuilderNameChange={handleBuilderNameChange}
          />

          {/* Enhanced Seller Credit Section */}
          <EnhancedSellerCreditSection 
            mortgageData={mortgageData}
            onSellerCreditChange={handleSellerCreditChange}
          />

          {/* Closing Costs List */}
          <ClosingCostsList 
            mortgageData={mortgageData}
            autoCalculate={autoCalculate.closingCosts}
            onClosingCostChange={handleClosingCostChange}
          />
          
          {/* Total with seller credit breakdown */}
          <ClosingCostTotal 
            total={mortgageData.closingCosts.total} 
            sellerCreditAmount={mortgageData.closingCosts.sellerCreditAmount}
          />
        </CardContent>
      </Card>

      {/* Earnest Money Deposit */}
      <EarnestMoneyInput
        value={mortgageData.earnestMoneyDeposit}
        onChange={(value) => setMortgageData(prev => ({ ...prev, earnestMoneyDeposit: value }))}
      />

      {/* Cash Due at Closing Summary */}
      <CashDueAtClosing mortgageData={mortgageData} />
    </div>
  );
};

export default ClosingCostsInputs;
